import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/src/renderer/components/ui/Button'
import { useSync } from '@/src/renderer/contexts/SyncProvider'
import { SYNC_PLANS } from './syncPlans'

type Billing = 'monthly' | 'yearly'

/**
 * The "Sync" section of the web Account page: a billing-period toggle + the
 * config-driven placeholder plan cards (Part C1). Real billing is not wired up
 * — every CTA triggers the free beta activation (`activateSync`), after which
 * the AccountPage observes the entitlement flip and redirects into the app.
 */
export function SyncPlansSection() {
  const { t } = useTranslation()
  const { activateSync } = useSync()
  const [billing, setBilling] = useState<Billing>('yearly')
  const [isActivating, setIsActivating] = useState(false)

  const handleActivate = async () => {
    setIsActivating(true)
    try {
      await activateSync()
      // On success `syncEntitlement` flips → AccountPage redirects to /home.
    } finally {
      setIsActivating(false)
    }
  }

  return (
    <section className="account-sync">
      <h2 className="account-sync__title">{t('account.sync.title')}</h2>
      <p className="account-sync__desc">{t('account.sync.description')}</p>

      <div className="account-sync__billing" role="group" aria-label={t('account.sync.title')}>
        {(['yearly', 'monthly'] as Billing[]).map((period) => (
          <button
            key={period}
            type="button"
            className={`account-sync__billing-option${billing === period ? ' account-sync__billing-option--active' : ''}`}
            aria-pressed={billing === period}
            onClick={() => setBilling(period)}
          >
            {t(period === 'yearly' ? 'account.sync.billingYearly' : 'account.sync.billingMonthly')}
          </button>
        ))}
      </div>

      <div className="account-sync__plans">
        {SYNC_PLANS.map((plan) => {
          const price = billing === 'yearly' ? plan.priceYearly : plan.priceMonthly
          const savePercent = plan.priceMonthly > 0
            ? Math.round(((plan.priceMonthly - plan.priceYearly) / plan.priceMonthly) * 100)
            : 0
          const featuresRaw = t(plan.featuresKey, { returnObjects: true }) as unknown
          const features = Array.isArray(featuresRaw) ? (featuresRaw as string[]) : []
          return (
            <div key={plan.id} className="account-plan">
              <div className="account-plan__name">{t(plan.nameKey)}</div>
              <div className="account-plan__price">
                <div className="account-plan__price-row">
                  <span className="account-plan__amount">{price} €</span>
                  {billing === 'yearly' && savePercent > 0 && (
                    <span className="account-plan__save">
                      {t('account.sync.save', { percent: savePercent })}
                    </span>
                  )}
                </div>
                <span className="account-plan__period">
                  {billing === 'yearly' ? t('account.sync.billedYearly') : t('account.sync.billedMonthly')}
                </span>
              </div>
              <ul className="account-plan__features">
                {features.map((feature, i) => (
                  <li key={i}>{feature}</li>
                ))}
              </ul>
              <Button onClick={handleActivate} disabled={isActivating} className="account-plan__cta">
                {isActivating ? t('account.sync.activating') : t('account.sync.activate')}
              </Button>
              <p className="account-plan__beta">{t('account.sync.betaFree')}</p>
            </div>
          )
        })}
      </div>
    </section>
  )
}
